import { Action, ActionReducer, createReducer, on } from '@ngrx/store';

import { CandidateProcedure, CandidateVerification } from '@interfaces';

import * as cvmActions from './cvm.actions';
import { CVMState, initialState } from './cvm.state';

const reducer: ActionReducer<CVMState, Action> = createReducer(
  initialState,

  on(
    cvmActions.searchCandidateVerifications,
    (state): CVMState => ({
      ...state,
      loadingCandidateVerifications: true,
      errorMessage: null,
    })
  ),
  on(
    cvmActions.searchCandidateVerificationsSuccess,
    (state, { candidateVerifications }): CVMState => ({
      ...state,
      loadingCandidateVerifications: false,
      candidateVerifications: candidateVerifications.data,
      candidateVerificationsPagination: candidateVerifications.pagination,
      errorMessage: null,
    })
  ),
  on(
    cvmActions.searchCandidateVerificationsError,
    (state, { errorMessage }): CVMState => ({
      ...state,
      loadingCandidateVerifications: false,
      errorMessage,
    })
  ),

  on(
    cvmActions.getCandidateVerificationDetails,
    (state): CVMState => ({
      ...state,
      loadingCandidateVerificationDetails: true,
      candidateVerificationDetails: null,
      errorMessage: null,
    })
  ),
  on(
    cvmActions.getCandidateVerificationDetailsSuccess,
    (state, { candidateVerification }): CVMState => ({
      ...state,
      loadingCandidateVerificationDetails: false,
      candidateVerificationDetails: candidateVerification,
      errorMessage: null,
    })
  ),
  on(
    cvmActions.getCandidateVerificationDetailsError,
    (state, { errorMessage }): CVMState => ({
      ...state,
      loadingCandidateVerificationDetails: false,
      errorMessage,
    })
  ),

  on(
    cvmActions.openCandidateVerificationFile,
    (state, { fileId }): CVMState => ({
      ...state,
      downloadingCandidateVerificationFileId: fileId,
      errorMessage: null,
    })
  ),
  on(
    cvmActions.openCandidateVerificationFileSuccess,
    (state, { fileId }): CVMState => ({
      ...state,
      downloadingCandidateVerificationFileId:
        state.downloadingCandidateVerificationFileId === fileId ? null : state.downloadingCandidateVerificationFileId,
      errorMessage: null,
    })
  ),
  on(
    cvmActions.openCandidateVerificationFileError,
    (state, { fileId, errorMessage }): CVMState => ({
      ...state,
      downloadingCandidateVerificationFileId:
        state.downloadingCandidateVerificationFileId === fileId ? null : state.downloadingCandidateVerificationFileId,
      errorMessage,
    })
  ),

  on(
    cvmActions.updateCandidateVerificationBasicDetails,
    (state): CVMState => ({
      ...state,
      loadingCandidateVerificationDetails: true,
      errorMessage: null,
    })
  ),
  on(
    cvmActions.updateCandidateVerificationBasicDetailsSuccess,
    (state, { candidateVerification }): CVMState => ({
      ...state,
      loadingCandidateVerificationDetails: false,
      candidateVerifications: state.candidateVerifications.map(item =>
        item.id === candidateVerification.id
          ? {
              ...item,
              companyId: candidateVerification.companyId,
              candidateDetails: {
                ...item.candidateDetails,
                ...candidateVerification.candidateDetails,
              },
              updatedAt: candidateVerification.updatedAt ?? item.updatedAt,
              updatedBy: candidateVerification.updatedBy ?? item.updatedBy,
            }
          : item
      ),
      candidateVerificationDetails:
        state.candidateVerificationDetails?.id === candidateVerification.id
          ? candidateVerification
          : state.candidateVerificationDetails,
      errorMessage: null,
    })
  ),
  on(
    cvmActions.updateCandidateVerificationBasicDetailsError,
    (state, { errorMessage }): CVMState => ({
      ...state,
      loadingCandidateVerificationDetails: false,
      errorMessage,
    })
  ),

  on(
    cvmActions.updateCandidateVerificationStatus,
    (state): CVMState => ({
      ...state,
      updatingCandidateVerificationStatus: true,
      errorMessage: null,
    })
  ),
  on(
    cvmActions.updateCandidateVerificationStatusSuccess,
    (state, { candidateVerification }): CVMState => ({
      ...state,
      updatingCandidateVerificationStatus: false,
      candidateVerifications: state.candidateVerifications.map(item =>
        item.id === candidateVerification.id
          ? {
              ...item,
              status: candidateVerification.status,
              result: candidateVerification.result,
              updatedAt: candidateVerification.updatedAt ?? item.updatedAt,
              updatedBy: candidateVerification.updatedBy ?? item.updatedBy,
            }
          : item
      ),
      candidateVerificationDetails:
        state.candidateVerificationDetails?.id === candidateVerification.id
          ? candidateVerification
          : state.candidateVerificationDetails,
      errorMessage: null,
    })
  ),
  on(
    cvmActions.updateCandidateVerificationStatusError,
    (state, { errorMessage }): CVMState => ({
      ...state,
      updatingCandidateVerificationStatus: false,
      errorMessage,
    })
  ),

  on(
    cvmActions.saveCandidateVerificationResult,
    (state): CVMState => ({
      ...state,
      loadingSavingCandidateVerificationResult: true,
      errorMessage: null,
    })
  ),
  on(
    cvmActions.saveCandidateVerificationResultSuccess,
    (state, { candidateVerification }): CVMState => ({
      ...state,
      loadingSavingCandidateVerificationResult: false,
      candidateVerifications: state.candidateVerifications.map(item =>
        item.id === candidateVerification.id
          ? {
              ...item,
              status: candidateVerification.status,
              result: candidateVerification.result,
              candidateDetailsForm: {
                ...item.candidateDetailsForm,
                ...candidateVerification.candidateDetailsForm,
              },
              updatedAt: candidateVerification.updatedAt ?? item.updatedAt,
              updatedBy: candidateVerification.updatedBy ?? item.updatedBy,
            }
          : item
      ),
      candidateVerificationDetails:
        state.candidateVerificationDetails?.id === candidateVerification.id
          ? candidateVerification
          : state.candidateVerificationDetails,
      errorMessage: null,
    })
  ),
  on(
    cvmActions.saveCandidateVerificationResultError,
    (state, { errorMessage }): CVMState => ({
      ...state,
      loadingSavingCandidateVerificationResult: false,
      errorMessage,
    })
  ),

  on(
    cvmActions.setCandidateVerificationDecision,
    (state): CVMState => ({
      ...state,
      errorMessage: null,
    })
  ),
  on(
    cvmActions.setCandidateVerificationDecisionSuccess,
    (state, { candidateVerification }): CVMState => ({
      ...state,
      candidateVerificationDetails:
        state.candidateVerificationDetails?.id === candidateVerification.id
          ? candidateVerification
          : state.candidateVerificationDetails,
      candidateVerifications: state.candidateVerifications.map(item =>
        item.id === candidateVerification.id
          ? {
              ...item,
              status: candidateVerification.status,
              result: candidateVerification.result,
              updatedAt: candidateVerification.updatedAt ?? item.updatedAt,
              updatedBy: candidateVerification.updatedBy ?? item.updatedBy,
            }
          : item
      ),
      errorMessage: null,
    })
  ),
  on(
    cvmActions.setCandidateVerificationDecisionError,
    (state, { errorMessage }): CVMState => ({
      ...state,
      errorMessage,
    })
  ),

  on(
    cvmActions.updateCandidateVerificationProcedureDecisionSuccess,
    (state, { candidateVerification }): CVMState => ({
      ...state,
      candidateVerificationDetails:
        state.candidateVerificationDetails?.id === candidateVerification.id
          ? candidateVerification
          : state.candidateVerificationDetails,
      candidateVerifications: state.candidateVerifications.map(item =>
        item.id === candidateVerification.id
          ? {
              ...item,
              status: candidateVerification.status,
              result: candidateVerification.result,
              updatedAt: candidateVerification.updatedAt ?? item.updatedAt,
              updatedBy: candidateVerification.updatedBy ?? item.updatedBy,
            }
          : item
      ),
      errorMessage: null,
    })
  ),
  on(
    cvmActions.updateCandidateVerificationProcedureDecisionError,
    (state, { errorMessage }): CVMState => ({
      ...state,
      errorMessage,
    })
  ),

  on(
    cvmActions.addCandidateVerificationExtraServiceSuccess,
    (state, { candidateVerification }): CVMState => ({
      ...state,
      candidateVerificationDetails:
        state.candidateVerificationDetails?.id === candidateVerification.id
          ? candidateVerification
          : state.candidateVerificationDetails,
      candidateVerifications: state.candidateVerifications.map(item =>
        item.id === candidateVerification.id
          ? {
              ...item,
              status: candidateVerification.status,
              result: candidateVerification.result,
              updatedAt: candidateVerification.updatedAt ?? item.updatedAt,
              updatedBy: candidateVerification.updatedBy ?? item.updatedBy,
            }
          : item
      ),
      errorMessage: null,
    })
  ),
  on(
    cvmActions.addCandidateVerificationExtraServiceError,
    (state, { errorMessage }): CVMState => ({
      ...state,
      errorMessage,
    })
  ),

  on(
    cvmActions.requestCandidateVerificationSupplementaryInfo,
    (state): CVMState => ({
      ...state,
      requestingCandidateVerificationSupplementaryInfo: true,
      errorMessage: null,
    })
  ),
  on(
    cvmActions.requestCandidateVerificationSupplementaryInfoSuccess,
    (state, { candidateVerification }): CVMState => ({
      ...state,
      requestingCandidateVerificationSupplementaryInfo: false,
      candidateVerifications: state.candidateVerifications.map(item =>
        item.id === candidateVerification.id
          ? {
              ...item,
              status: candidateVerification.status ?? item.status,
              candidateDetailsForm: {
                ...item.candidateDetailsForm,
                ...candidateVerification.candidateDetailsForm,
              },
              updatedAt: candidateVerification.updatedAt ?? item.updatedAt,
              updatedBy: candidateVerification.updatedBy ?? item.updatedBy,
            }
          : item
      ),
      candidateVerificationDetails:
        state.candidateVerificationDetails?.id === candidateVerification.id
          ? {
              ...state.candidateVerificationDetails,
              ...candidateVerification,
              candidateDetails: {
                ...state.candidateVerificationDetails?.candidateDetails,
                ...candidateVerification?.candidateDetails,
              },
              candidateDetailsForm: {
                ...state.candidateVerificationDetails?.candidateDetailsForm,
                ...candidateVerification?.candidateDetailsForm,
              },
            }
          : state.candidateVerificationDetails,
      errorMessage: null,
    })
  ),
  on(
    cvmActions.requestCandidateVerificationSupplementaryInfoError,
    (state, { errorMessage }): CVMState => ({
      ...state,
      requestingCandidateVerificationSupplementaryInfo: false,
      errorMessage,
    })
  ),

  on(
    cvmActions.regenerateCandidateFormLinkAndPin,
    (state): CVMState => ({
      ...state,
      loadingCandidateVerificationDetails: true,
      errorMessage: null,
    })
  ),
  on(
    cvmActions.regenerateCandidateFormLinkAndPinSuccess,
    (state, { candidateVerification }): CVMState => ({
      ...state,
      loadingCandidateVerificationDetails: false,
      candidateVerifications: state.candidateVerifications.map(item =>
        item.id === candidateVerification.id
          ? {
              ...item,
              companyId: candidateVerification.companyId,
              status: candidateVerification.status,
              result: candidateVerification.result,
              candidateDetails: {
                ...item.candidateDetails,
                ...candidateVerification.candidateDetails,
              },
              candidateDetailsForm: {
                ...item.candidateDetailsForm,
                ...candidateVerification.candidateDetailsForm,
              },
              updatedAt: candidateVerification.updatedAt ?? item.updatedAt,
              updatedBy: candidateVerification.updatedBy ?? item.updatedBy,
            }
          : item
      ),
      candidateVerificationDetails:
        state.candidateVerificationDetails?.id === candidateVerification.id
          ? candidateVerification
          : state.candidateVerificationDetails,
      errorMessage: null,
    })
  ),
  on(
    cvmActions.regenerateCandidateFormLinkAndPinError,
    (state, { errorMessage }): CVMState => ({
      ...state,
      loadingCandidateVerificationDetails: false,
      errorMessage,
    })
  ),

  on(
    cvmActions.extendCandidateVerificationFormValidity,
    (state): CVMState => ({
      ...state,
      loadingCandidateVerificationDetails: true,
      errorMessage: null,
    })
  ),
  on(
    cvmActions.extendCandidateVerificationFormValiditySuccess,
    (state, { candidateVerification }): CVMState => ({
      ...state,
      loadingCandidateVerificationDetails: false,
      candidateVerificationDetails: {
        ...state.candidateVerificationDetails,
        ...candidateVerification,
        candidateDetails: {
          ...state.candidateVerificationDetails?.candidateDetails,
          ...candidateVerification?.candidateDetails,
        },
        candidateDetailsForm: {
          ...state.candidateVerificationDetails?.candidateDetailsForm,
          ...candidateVerification?.candidateDetailsForm,
        },
      },
      errorMessage: null,
    })
  ),
  on(
    cvmActions.extendCandidateVerificationFormValidityError,
    (state, { errorMessage }): CVMState => ({
      ...state,
      loadingCandidateVerificationDetails: false,
      errorMessage,
    })
  ),

  on(
    cvmActions.deleteCandidateVerification,
    (state): CVMState => ({
      ...state,
      deletingCandidateVerification: true,
      errorMessage: null,
    })
  ),
  on(
    cvmActions.deleteCandidateVerificationSuccess,
    (state, { deletedCandidateVerificationId }): CVMState => ({
      ...state,
      deletingCandidateVerification: false,
      candidateVerifications: state.candidateVerifications.filter(
        candidateVerification => candidateVerification.id !== deletedCandidateVerificationId
      ),
      candidateVerificationsPagination: {
        ...state.candidateVerificationsPagination,
        all: Math.max(0, (state.candidateVerificationsPagination?.all || 0) - 1),
        count: Math.max(0, (state.candidateVerificationsPagination?.count || 0) - 1),
      },
      candidateVerificationDetails:
        state.candidateVerificationDetails?.id === deletedCandidateVerificationId
          ? null
          : state.candidateVerificationDetails,
      errorMessage: null,
    })
  ),
  on(
    cvmActions.deleteCandidateVerificationError,
    (state, { errorMessage }): CVMState => ({
      ...state,
      deletingCandidateVerification: false,
      errorMessage,
    })
  ),

  on(
    cvmActions.getCandidateVerificationHistory,
    (state): CVMState => ({
      ...state,
      candidateVerificationHistory: null,
      loadingCandidateVerificationHistory: true,
      errorMessage: null,
    })
  ),
  on(
    cvmActions.getCandidateVerificationHistorySuccess,
    (state, { candidateVerificationHistory }): CVMState => ({
      ...state,
      candidateVerificationHistory,
      loadingCandidateVerificationHistory: false,
      errorMessage: null,
    })
  ),
  on(
    cvmActions.getCandidateVerificationHistoryError,
    (state, { errorMessage }): CVMState => ({
      ...state,
      loadingCandidateVerificationHistory: false,
      errorMessage,
    })
  )
);

export function cvmReducer(state: CVMState | undefined, action: Action): CVMState {
  return reducer(state, action);
}

function mergeUpdatedProcedureOnVerification(
  verification: Partial<CandidateVerification> | null,
  procedure: CandidateProcedure
): Partial<CandidateVerification> | null {
  if (!verification) {
    return verification;
  }

  const currentProcedures = verification.proceduresBeforeHire ?? [];
  const matchingProcedureExists = currentProcedures.some(item => item.id === procedure.id);

  return {
    ...verification,
    proceduresBeforeHire: matchingProcedureExists
      ? currentProcedures.map(item => (item.id === procedure.id ? procedure : item))
      : [...currentProcedures, procedure],
    updatedAt: procedure.updatedAt ?? verification.updatedAt,
  };
}
